import React from 'react';
import NavigationHeader from '../components/NavigationHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Globe, Users, MessageCircle, Trophy, Video, Monitor, 
  Clock, GraduationCap, CheckCircle2, Sparkles, ArrowRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const EnglishImmersionPage = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const immersionFeatures = [
    { icon: GraduationCap, titleKey: 'classroomTeaching', descKey: 'classroomTeachingDesc' },
    { icon: Trophy, titleKey: 'footballWorkshops', descKey: 'footballWorkshopsDesc' },
    { icon: MessageCircle, titleKey: 'clubPresentations', descKey: 'clubPresentationsDesc' },
    { icon: Video, titleKey: 'videoAnalysis', descKey: 'videoAnalysisDesc' },
    { icon: Monitor, titleKey: 'trainingSessions', descKey: 'trainingSessionsDesc' },
    { icon: Users, titleKey: 'internationalEnvironment', descKey: 'internationalEnvironmentDesc' },
    { icon: Sparkles, titleKey: 'englishMonitors', descKey: 'englishMonitorsDesc' },
    { icon: Globe, titleKey: 'freeTimeActivities', descKey: 'freeTimeActivitiesDesc' }
  ];

  const images = {
    hero: "https://customer-assets.emergentagent.com/job_proficiency-camp/artifacts/oa17gkqh_Academy_Camp_2025-721.jpg",
    classroom1: "https://customer-assets.emergentagent.com/job_proficiency-camp/artifacts/iuykrksc_Academy_Camp_2025-720.jpg",
    classroom2: "https://customer-assets.emergentagent.com/job_proficiency-camp/artifacts/l48lr4iz_Academy_Camp_2025-15.jpg",
    presentation: "https://customer-assets.emergentagent.com/job_proficiency-camp/artifacts/dactqplz_Practica-Conferencia-de-Prensa-2048x1356.webp",
    workshop: "https://customer-assets.emergentagent.com/job_proficiency-camp/artifacts/ya9qaizy_Academy_Camp_2025-14.jpg",
    students: "https://customer-assets.emergentagent.com/job_proficiency-camp/artifacts/l7tqiaa7__DSC4765.webp"
  };

  return (
    <div className="min-h-screen">
      <NavigationHeader />
      
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${images.hero})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-green-700/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-4xl">
            <div className="inline-flex items-center bg-blue-600/20 backdrop-blur-sm border border-blue-500/30 rounded-full px-6 py-2 mb-6">
              <Globe className="h-5 w-5 mr-2" />
              <span className="font-bold text-sm uppercase tracking-wider">{t('englishImmersion.badge')}</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              {t('englishImmersion.heroTitle')}
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-gray-200 font-medium">
              {t('englishImmersion.heroSubtitle')}
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3">
                <span className="font-bold">{t('englishImmersion.countries')}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3">
                <span className="font-bold">{t('englishImmersion.englishEnvironment')}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3">
                <span className="font-bold">{t('englishImmersion.teflCertified')}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/prices">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-900 hover:bg-gray-100 font-bold text-lg px-10 py-7 rounded-xl transform hover:scale-105 transition-all duration-200 shadow-2xl"
                >
                  {t('englishImmersion.viewPrograms')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/signup">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold text-lg px-10 py-7 rounded-xl transform hover:scale-105 transition-all duration-200"
                >
                  {t('englishImmersion.signUpNow')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                {t('englishImmersion.moreThanClasses')}
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                {t('englishImmersion.moreThanClassesDesc1')}
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('englishImmersion.moreThanClassesDesc2')}
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                <p className="text-gray-800 font-semibold">
                  {t('englishImmersion.learningQuote')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <img 
                src={images.classroom1} 
                alt="English Classroom" 
                className="rounded-xl shadow-lg w-full h-64 object-cover"
              />
              <img 
                src={images.classroom2} 
                alt="Students Learning" 
                className="rounded-xl shadow-lg w-full h-64 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 24/7 Immersion Features */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('englishImmersion.immersionTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('englishImmersion.immersionDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {immersionFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={index}
                  className="p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 bg-white"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-green-100 rounded-full flex items-center justify-center mb-4">
                    <Icon className="h-7 w-7 text-blue-700" />
                  </div>
                  <h3 className="text-lg font-black text-gray-900 mb-2">
                    {t(`englishImmersion.${feature.titleKey}`)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t(`englishImmersion.${feature.descKey}`)}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Professional English Classes */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <GraduationCap className="h-16 w-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('englishImmersion.professionalClasses')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('englishImmersion.professionalClassesDesc')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            <div className="space-y-6">
              <Card className="p-8 bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
                <Clock className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="text-2xl font-black text-gray-900 mb-4">{t('englishImmersion.classSchedule')}</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>{t('englishImmersion.hoursPerDay')}</strong></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>{t('englishImmersion.teflCertifiedTeachers')}</strong></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>{t('englishImmersion.footballFocusedContext')}</strong></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>{t('englishImmersion.communicativeApproach')}</strong></span>
                  </li>
                </ul>
              </Card>

              <Card className="p-8 bg-gradient-to-br from-green-50 to-white border-2 border-green-200">
                <MessageCircle className="h-10 w-10 text-green-600 mb-4" />
                <h3 className="text-2xl font-black text-gray-900 mb-4">{t('englishImmersion.teachingMethodology')}</h3>
                <p className="text-gray-700 mb-4">
                  {t('englishImmersion.teachingMethodologyDesc1')}
                </p>
                <p className="text-gray-700">
                  {t('englishImmersion.teachingMethodologyDesc2')}
                </p>
              </Card>
            </div>

            <div>
              <img 
                src={images.workshop} 
                alt="English Workshop" 
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Classroom Activities */}
          <Card className="p-8 bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200">
            <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">{t('englishImmersion.outsideClassroom')}</h3>
            <p className="text-center text-gray-600 mb-6">
              {t('englishImmersion.outsideClassroomDesc')}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{t('englishImmersion.rolePlayingGames')}</span>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{t('englishImmersion.teamworkExercises')}</span>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{t('englishImmersion.funCreativeActivities')}</span>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{t('englishImmersion.practicalActivities')}</span>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Sports as Learning Tool */}
      <section className="py-20 bg-gradient-to-br from-green-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Trophy className="h-16 w-16 mb-6" />
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                {t('englishImmersion.sportsContinuousLearning')}
              </h2>
              <div className="space-y-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-black mb-3">{t('englishImmersion.duringTraining')}</h3>
                  <p className="text-green-100">
                    {t('englishImmersion.duringTrainingDesc')}
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-black mb-3">{t('englishImmersion.dailyCampLife')}</h3>
                  <p className="text-green-100">
                    {t('englishImmersion.dailyCampLifeDesc')}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <img 
                src={images.presentation} 
                alt="Football Presentation" 
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* International Community */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={images.students} 
                alt="International Students" 
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>

            <div className="order-1 lg:order-2">
              <Users className="h-16 w-16 text-blue-600 mb-6" />
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                {t('englishImmersion.multiculturalSpace')}
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                {t('englishImmersion.multiculturalSpaceDesc1')}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t('englishImmersion.multiculturalSpaceDesc2')}
              </p>

              <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200">
                <h3 className="text-xl font-black text-gray-900 mb-4">{t('englishImmersion.naturalCommunication')}</h3>
                <p className="text-gray-700">
                  {t('englishImmersion.naturalCommunicationDesc')}
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Goals */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Sparkles className="h-16 w-16 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              {t('englishImmersion.ourGoal')}
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t('englishImmersion.ourGoalDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20 text-center">
              <CheckCircle2 className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
              <p className="font-bold">{t('englishImmersion.improveEnglish')}</p>
            </Card>
            <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20 text-center">
              <CheckCircle2 className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
              <p className="font-bold">{t('englishImmersion.gainConfidence')}</p>
            </Card>
            <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20 text-center">
              <CheckCircle2 className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
              <p className="font-bold">{t('englishImmersion.developIntercultural')}</p>
            </Card>
            <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20 text-center">
              <CheckCircle2 className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
              <p className="font-bold">{t('englishImmersion.bePartOfCommunity')}</p>
            </Card>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-black mb-4">{t('englishImmersion.completeImmersion')}</h3>
            <p className="text-lg text-blue-100 mb-6">
              {t('englishImmersion.completeImmersionDesc')}
            </p>
            <p className="text-xl font-bold text-white">
              {t('englishImmersion.safeEnvironment')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black text-gray-900 mb-6">
            {t('englishImmersion.readyToExperience')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('englishImmersion.readyToExperienceDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prices">
              <Button 
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-10 py-6"
              >
                {t('englishImmersion.viewProgramsPrices')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/signup">
              <Button 
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white font-bold text-lg px-10 py-6"
              >
                {t('englishImmersion.signUpNow')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EnglishImmersionPage;
